import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    PlusCircle,
    Search,
    Eye,
    Edit2,
    Trash2,
    Loader2,
    AlertCircle,
    Play,
    Clock,
    LayoutList,
    ChevronLeft, ChevronRight,
    Tag, Check, X,
} from 'lucide-react';
import { PraticaService } from '../services/apiService';
import { Pratica, PreflightEliminazione, UserRole } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/ToastContext";
import ResponsiveTable from '../components/ResponsiveTable';

type Tab = 'in-corso' | 'da-attivare' | 'tutte';

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);

const PraticheView: React.FC = () => {
    const { user, currentSedeId } = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [pratiche, setPratiche] = useState<Pratica[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [activeTab, setActiveTab] = useState<Tab>('in-corso');
    const [filtro, setFiltro] = useState('');
    const [stato, setStato] = useState('');
    const [tipoMandato, setTipoMandato] = useState('');

    const [praticaToDelete, setPraticaToDelete] = useState<Pratica | null>(null);
    // Modifica inline del riferimento: riga in editing + valore corrente.
    // Il controllo canEditRiferimento è solo lato client, il BE verifica comunque i permessi.
    const [editingRifId, setEditingRifId] = useState<number | string | null>(null);
    const [editingRifValue, setEditingRifValue] = useState<string>('');
    const [savingRifId, setSavingRifId] = useState<number | string | null>(null);
    const canEditRiferimento = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;
    // Cosa fare con preventivi/proforma collegati: di default vengono scollegati
    // e restano in /preventivi.
    const [cascadePreventiviOnDelete, setCascadePreventiviOnDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Eliminando il mandato se ne azzerano gli importi (il JSON viene svuotato per
    // anonimizzare il soggetto). Se c'è un residuo non riscosso va mostrato prima.
    const [preflight, setPreflight] = useState<PreflightEliminazione | null>(null);
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [residuoAccettato, setResiduoAccettato] = useState(false);

    useEffect(() => {
        if (!praticaToDelete) { setPreflight(null); setResiduoAccettato(false); return; }
        let cancelled = false;
        (async () => {
            setPreflightLoading(true);
            try {
                const res = await PraticaService.preflightEliminazione(Number(praticaToDelete.id));
                if (!cancelled && res.success) setPreflight(res.data);
            } catch (e) {
                // Preflight non disponibile: non blocchiamo, ma non mostriamo importi non verificati.
                console.error('Errore preflight eliminazione mandato', e);
            } finally {
                if (!cancelled) setPreflightLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [praticaToDelete]);

    // Basta che manchi l'incasso o la fattura per chiedere una conferma esplicita.
    const haEsposizione = preflight?.haResiduo === true || preflight?.haDaFatturare === true;
    // Report o spese in attesa sugli incarichi: il backend rifiuta l'eliminazione.
    const bloccoInAttesa = preflight?.messaggioBlocco || null;
    const deleteBloccato = isDeleting || preflightLoading || !!bloccoInAttesa || (haEsposizione && !residuoAccettato);

    const [currentPage, setCurrentPage] = useState(0);
    const [pagination, setPagination] = useState({ totalPages: 0, totalElements: 0 });
    const [tabCounts, setTabCounts] = useState({ inCorso: 0, daAttivare: 0, tutte: 0 });
    const pageSize = 10;

    useEffect(() => {
        // Una risposta arrivata dopo che filtri o pagina sono cambiati non sovrascrive quella nuova.
        let attuale = true;
        const fetchPratiche = async () => {
            setIsLoading(true);
            try {
                // Chiamata al backend con tutti i parametri di filtro e paginazione
                const response = await PraticaService.getAll({
                    anno: selectedYear,
                    tab: activeTab,
                    search: filtro,
                    sedeId: currentSedeId === 'ALL' ? undefined : Number(currentSedeId),
                    page: currentPage,
                    size: pageSize,
                    modelId: tipoMandato || undefined,
                    stato: stato || undefined,
                });

                // La risposta è una Page di Spring
                const pageData = response.data;
                if (!attuale) return;
                setPratiche(pageData.content || []);
                setPagination({
                    totalPages: pageData.totalPages,
                    totalElements: pageData.totalElements
                });
                if (pageData.tabCounts) setTabCounts(pageData.tabCounts);
            } catch (err) {
                if (attuale) setError("Errore nel caricamento dei dati.");
            } finally {
                if (attuale) setIsLoading(false);
            }
        };

        const timer = setTimeout(fetchPratiche, filtro ? 400 : 0);
        return () => { attuale = false; clearTimeout(timer); };
    }, [selectedYear, activeTab, filtro, currentSedeId, currentPage, tipoMandato, stato]);

    const handleDeleteConfirm = async () => {
        if (!praticaToDelete) return;
        setIsDeleting(true);
        try {
            await PraticaService.delete(Number(praticaToDelete.id), cascadePreventiviOnDelete);
            setPratiche(prev => prev.filter(p => p.id !== praticaToDelete.id));
            addToast(
                cascadePreventiviOnDelete
                    ? "Mandato e preventivi/proforma collegati eliminati"
                    : "Mandato eliminato (preventivi/proforma scollegati)",
                "success",
            );
            setPraticaToDelete(null);
            setCascadePreventiviOnDelete(false);
        } catch (err: any) {
            console.error("Errore durante l'eliminazione:", err);
            addToast(err?.response?.data?.message || "Errore durante l'eliminazione del mandato", "error");
        } finally {
            setIsDeleting(false);
        }
    };

    // Il filtro per ruolo è applicato dal backend (PraticaService.findFilteredList):
    // PraticaListDTO non espone sedeId, quindi un filtro lato FE non funzionerebbe.

    // Ricerca, tab, tipo e stato sono già applicati dal backend (anche sui dati del cliente e sul
    // numero del mandato): la pagina mostra la risposta così com'è, senza rifiltrarla.
    const activeList = pratiche;

    // Badge accanto al cliente se eliminato o anonimizzato; anonimizzato ha priorità
    // perché implica anche eliminato.
    const clienteBadge = (p: Pratica) => {
        if (p.clienteAnonymized) {
            return <span className="ml-2 inline-block text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded" title="Cliente anonimizzato ai sensi del GDPR (post-6 mesi da eliminazione)">Anonimizzato</span>;
        }
        if (p.clienteDeleted) {
            return <span className="ml-2 inline-block text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded" title="Cliente eliminato: la riapertura del mandato è bloccata">Cliente eliminato</span>;
        }
        return null;
    };

    const renderCliente = (p: Pratica) => {
        // Azienda: "Referente (Ragione Sociale)" da clienteReferente; Privato: solo il nome.
        const tipo = (p as any).clienteTipo || (p as any).clientTipo;
        const ref = (p as any).clienteReferente || (p as any).clientReferente;
        const nome = p.clientName || (p as any).clienteNome;
        const badge = clienteBadge(p);
        if (tipo === 'Azienda' && ref && nome) {
            return <span className="font-bold text-gray-800">{ref} <span className="font-medium text-gray-500">({nome})</span>{badge}</span>;
        }
        if (nome) return <span className="font-bold text-gray-800">{nome}{badge}</span>;
        const c = Array.isArray(p.client) ? p.client[0] : (p.client as any);
        if (c) {
            if (c.tipo === 'Azienda' && c.ragioneSociale) {
                const referente = `${c.nome || ''} ${c.surname || ''}`.trim();
                return referente
                    ? <span className="font-bold text-gray-800">{referente} <span className="font-medium text-gray-500">({c.ragioneSociale})</span>{badge}</span>
                    : <span className="font-semibold">{c.ragioneSociale}{badge}</span>;
            }
            return <span>{c.nome} {c.surname}{badge}</span>;
        }
        return <span className="text-gray-400 italic">Nessun cliente</span>;
    };

    const TIPO_CONFIG: Record<string, { label: string; color: string }> = {
        'prev-standard':      { label: 'Privati',       color: 'bg-blue-100 text-blue-700 border-blue-200' },
        'mand-investigativo': { label: 'Investigativo', color: 'bg-orange-100 text-orange-700 border-orange-200' },
        'prev-bonifiche':     { label: 'Bonifiche',     color: 'bg-purple-100 text-purple-700 border-purple-200' },
        'prev-consulenza':    { label: 'Consulenza',    color: 'bg-teal-100 text-teal-700 border-teal-200' },
    };

    const getMandatoType = (p: Pratica) => TIPO_CONFIG[(p as any).modelId] ?? null;

    // Modifica inline del riferimento: matita → input con conferma/annulla
    // (Invio conferma, Esc annulla).
    const startEditRiferimento = (p: Pratica) => {
        setEditingRifId(p.id);
        setEditingRifValue(((p as any).riferimento as string) || '');
    };
    const cancelEditRiferimento = () => {
        setEditingRifId(null);
        setEditingRifValue('');
    };
    const saveEditRiferimento = async (p: Pratica) => {
        const idNum = Number(p.id);
        if (Number.isNaN(idNum)) return;
        setSavingRifId(p.id);
        try {
            const trimmed = editingRifValue.trim().slice(0, 120);
            const res: any = await PraticaService.patchRiferimento(idNum, trimmed);
            const updatedRif = (res?.data?.riferimento ?? trimmed) || null;
            setPratiche(prev => prev.map(x => x.id === p.id ? { ...x, riferimento: updatedRif } as any : x));
            addToast("Riferimento aggiornato", "success");
            cancelEditRiferimento();
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Errore aggiornamento riferimento", "error");
        } finally {
            setSavingRifId(null);
        }
    };

    const getStatusColor = (stato?: string) => {
        const s = (stato || '').toUpperCase();
        if (s === 'BOZZA') return 'bg-slate-200 text-slate-700 border-slate-300';
        if (s.includes('CORSO')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        if (s.includes('CONCLUS') || s.includes('COMPLETAT')) return 'bg-gray-200 text-gray-700 border-gray-300';
        if (s.includes('SOSPES')) return 'bg-amber-100 text-amber-800 border-amber-200';
        if (s.includes('ANNULLAT')) return 'bg-rose-100 text-rose-800 border-rose-200';
        if (s.includes('ATTESA')) return 'bg-blue-100 text-blue-800 border-blue-200';
        if (s.includes('ATTIVARE')) return 'bg-indigo-100 text-indigo-800 border-indigo-200';
        return 'bg-slate-100 text-slate-700 border-slate-200';
    };

    const tabs: { id: Tab; label: string; count: number; icon: React.ReactNode; accent: string; iconBg: string }[] = [
        {
            id: 'in-corso',
            label: 'In Corso',
            count: tabCounts.inCorso,
            icon: <Play size={18} />,
            accent: 'border-emerald-500 text-emerald-700',
            iconBg: 'bg-emerald-100 text-emerald-600',
        },
        {
            id: 'da-attivare',
            label: 'Da Attivare',
            count: tabCounts.daAttivare,
            icon: <Clock size={18} />,
            accent: 'border-indigo-500 text-indigo-700',
            iconBg: 'bg-indigo-100 text-indigo-600',
        },
        {
            id: 'tutte',
            label: 'Tutte',
            count: tabCounts.tutte,
            icon: <LayoutList size={18} />,
            accent: 'border-gray-500 text-gray-700',
            iconBg: 'bg-gray-100 text-gray-600',
        },
    ];

    return (
        <div className="p-6 space-y-6 relative">
            {/* Intestazione */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Servizi Investigativi</h1>
                <button
                    onClick={() => navigate('/pratiche/nuova')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 shadow-sm transition-all"
                >
                    <PlusCircle size={18} /> Nuovo Mandato
                </button>
            </div>

            {/* Tab */}
            <div className="grid grid-cols-3 gap-4">
                {tabs.map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setStato(''); setTipoMandato(''); setCurrentPage(0); }}
                            className={`bg-white rounded-xl border-2 p-4 flex items-center gap-3 text-left transition-all shadow-sm hover:shadow-md ${
                                isActive ? tab.accent + ' shadow-md' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            <div className={`p-2.5 rounded-xl flex-shrink-0 ${isActive ? tab.iconBg : 'bg-gray-100 text-gray-400'}`}>
                                {tab.icon}
                            </div>
                            <div>
                                <p className={`text-2xl font-bold leading-none mb-1 ${isActive ? '' : 'text-gray-800'}`}>
                                    {isLoading ? '–' : tab.count}
                                </p>
                                <p className={`text-xs font-semibold uppercase tracking-wide ${isActive ? '' : 'text-gray-500'}`}>
                                    {tab.label}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Ricerca + filtro stato (solo tab "tutte") */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Cerca per nome mandato o cliente..."
                        className="w-full pl-10 pr-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-shadow font-medium"
                        value={filtro}
                        onChange={e => { setFiltro(e.target.value); setCurrentPage(0); }}
                    />
                </div>
                <select
                    className="px-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer font-medium"
                    value={tipoMandato}
                    onChange={e => { setTipoMandato(e.target.value); setCurrentPage(0); }}
                >
                    <option value="">Tutti i tipi</option>
                    <option value="mand-investigativo">Investigativo</option>
                    <option value="prev-bonifiche">Bonifiche</option>
                    <option value="prev-consulenza">Consulenza</option>
                </select>

                {activeTab === 'tutte' && (
                    <select
                        className="px-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer font-medium"
                        value={stato}
                        onChange={e => { setStato(e.target.value); setCurrentPage(0); }}
                    >
                        <option value="">Tutti gli stati</option>
                        <option value="BOZZA">Bozze Salvate</option>
                        <option value="Da Attivare">Da Attivare</option>
                        <option value="In Corso">In Corso</option>
                        <option value="In Attesa di Firma">In Attesa di Firma</option>
                        <option value="Firmata">Firmata</option>
                        <option value="Sospesa">Sospesa</option>
                        <option value="Conclusa">Conclusa</option>
                        <option value="Annullata">Annullata</option>
                    </select>
                )}
            </div>

            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <button
                        key={year}
                        onClick={() => { setSelectedYear(year); setCurrentPage(0); }}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                            selectedYear === year ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200'
                        }`}
                    >
                        {year}
                    </button>
                ))}
            </div>

            {/* Tabella */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[300px]">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500 gap-2">
                        <Loader2 className="animate-spin text-blue-600" size={32} />
                        <p className="font-medium">Caricamento mandati in corso...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64 text-red-500 gap-2">
                        <AlertCircle size={32} />
                        <p className="font-medium">{error}</p>
                        <button onClick={() => window.location.reload()} className="text-sm underline font-bold">Riprova</button>
                    </div>
                ) : (
                    <ResponsiveTable
                        data={activeList}
                        keyExtractor={(p) => p.id}
                        emptyMessage="Nessun mandato trovato."
                        rowClassName={(p) => p.stato === 'BOZZA' ? 'bg-slate-50/50 hover:bg-slate-100' : ''}
                        columns={[
                            {
                                key: 'id',
                                label: 'ID',
                                hideOn: 'mobile',
                                render: (p) => <span className="font-mono text-sm text-gray-500">#{p.id}</span>,
                            },
                            {
                                key: 'nome',
                                label: 'Nome Mandato',
                                render: (p) => (
                                    <div
                                        className="cursor-pointer"
                                        onClick={() => p.stato === 'BOZZA' ? navigate(`/pratiche/modifica/${p.id}`) : navigate(`/pratiche/${p.id}`)}
                                    >
                                        <div className="font-bold text-blue-700 hover:underline leading-snug">
                                            {p.name || p.nome || "Senza Nome"}
                                            {p.stato === 'BOZZA' && <span className="ml-2 text-[10px] text-slate-500 font-medium">(Continua compilazione...)</span>}
                                        </div>
                                        {(() => {
                                            const tipo = getMandatoType(p);
                                            return tipo ? (
                                                <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${tipo.color}`}>
                                                    {tipo.label}
                                                </span>
                                            ) : null;
                                        })()}
                                    </div>
                                ),
                            },
                            {
                                key: 'cliente',
                                label: 'Cliente',
                                render: (p) => <span className="text-gray-700">{renderCliente(p)}</span>,
                            },
                            {
                                // Riferimento libero del mandato, modificabile inline solo da titolare/responsabile.
                                key: 'riferimento',
                                label: 'Riferimento',
                                render: (p) => {
                                    const isEditing = editingRifId === p.id;
                                    const isSaving = savingRifId === p.id;
                                    const value = (p as any).riferimento as string | undefined;
                                    if (isEditing) {
                                        return (
                                            <div className="inline-flex items-center gap-1">
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    maxLength={120}
                                                    value={editingRifValue}
                                                    onChange={e => setEditingRifValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') { e.preventDefault(); saveEditRiferimento(p); }
                                                        else if (e.key === 'Escape') { e.preventDefault(); cancelEditRiferimento(); }
                                                    }}
                                                    disabled={isSaving}
                                                    placeholder="Es. Milano / Sig. Rossi"
                                                    className="w-44 px-2 py-1 text-sm border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-gray-100"
                                                />
                                                <button
                                                    onClick={() => saveEditRiferimento(p)}
                                                    disabled={isSaving}
                                                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50"
                                                    title="Salva"
                                                >
                                                    {isSaving ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                                                </button>
                                                <button
                                                    onClick={cancelEditRiferimento}
                                                    disabled={isSaving}
                                                    className="p-1 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50"
                                                    title="Annulla (Esc)"
                                                >
                                                    <X size={14}/>
                                                </button>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="inline-flex items-center gap-1.5">
                                            <span
                                                className="text-gray-700 inline-flex items-center gap-1.5 text-sm font-medium max-w-[180px]"
                                                title={value || ''}
                                            >
                                                <Tag className="w-3.5 h-3.5 text-amber-500 shrink-0"/>
                                                <span className="truncate">{value || '-'}</span>
                                            </span>
                                            {canEditRiferimento && (
                                                <button
                                                    onClick={() => startEditRiferimento(p)}
                                                    className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                                    title={value ? 'Modifica riferimento' : 'Imposta riferimento'}
                                                >
                                                    <Edit2 size={12}/>
                                                </button>
                                            )}
                                        </div>
                                    );
                                },
                            },
                            {
                                key: 'stato',
                                label: 'Stato',
                                render: (p) => (
                                    <span className={`inline-block whitespace-nowrap px-1.5 sm:px-2.5 py-1 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-normal sm:tracking-wider border shadow-sm ${getStatusColor(p.stato)}`}>
                                        {p.stato || 'Da Attivare'}
                                    </span>
                                ),
                            },
                            {
                                key: 'createdAt',
                                label: 'Creata il',
                                hideOn: 'mobile',
                                render: (p) => (
                                    <span className="text-sm text-gray-500 font-medium">
                                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : '-'}
                                    </span>
                                ),
                            },
                            {
                                key: 'actions',
                                label: 'Azioni',
                                align: 'right',
                                render: (p) => (
                                    <div className="flex justify-end gap-2">
                                        {p.stato === 'BOZZA' ? (
                                            <button
                                                onClick={() => navigate(`/pratiche/modifica/${p.id}`)}
                                                className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                                                title="Riprendi Bozza"
                                            >
                                                <Edit2 size={16} strokeWidth={2.5}/>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => navigate(`/pratiche/${p.id}`)}
                                                className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                                                title="Vedi Dettagli"
                                            >
                                                <Eye size={16} strokeWidth={2.5}/>
                                            </button>
                                        )}
                                        {/* Il responsabile elimina i mandati delle sedi che gestisce: la lista
                                            contiene solo quelli e il server rifiuta le altre sedi. */}
                                        {(user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE) && (
                                            <button
                                                onClick={() => setPraticaToDelete(p)}
                                                className="p-2 text-rose-400 hover:bg-rose-50 hover:text-rose-600 rounded-lg transition-colors border border-transparent hover:border-rose-200"
                                                title="Elimina"
                                            >
                                                <Trash2 size={16} strokeWidth={2.5}/>
                                            </button>
                                        )}
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </div>

            {!isLoading && pagination.totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-4 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                        Mostrando <b>{pratiche.length}</b> di <b>{pagination.totalElements}</b> mandati
                    </div>
                    <div className="flex gap-2">
                        <button
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className="p-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <div className="flex items-center px-4 text-sm font-bold">
                            Pagina {currentPage + 1} di {pagination.totalPages}
                        </div>
                        <button
                            disabled={currentPage >= pagination.totalPages - 1}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            )}

            {/* Modale eliminazione */}
            {praticaToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
                    {/* Alta al massimo quanto lo schermo: scorre il contenuto, i pulsanti restano visibili. */}
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden border border-slate-100">
                        <div className="p-6 overflow-y-auto min-h-0">
                            <div className="flex items-center gap-3 mb-4 text-rose-600">
                                <div className="p-3 bg-rose-100 rounded-full">
                                    <AlertCircle size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900">Elimina Mandato</h3>
                            </div>
                            <p className="text-slate-600 mb-2 font-medium">
                                Sei sicuro di voler eliminare <strong className="text-slate-900">{praticaToDelete.name || praticaToDelete.nome}</strong>?
                            </p>
                            <div className="bg-rose-50 p-4 rounded-xl border border-rose-100 mt-5 text-sm text-rose-800 leading-relaxed">
                                <strong>Attenzione:</strong> Questa azione rimuoverà il mandato dalla visualizzazione. Tutti i dati associati come incarichi, report e file verranno considerati archiviati/eliminati. Questa azione è irreversibile.
                            </div>

                            {/* L'importo non riscosso va mostrato prima di eliminare. */}
                            {preflightLoading && (
                                <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                                    <Loader2 className="animate-spin" size={16}/>
                                    Verifica importi non riscossi o non fatturati…
                                </div>
                            )}
                            {!preflightLoading && bloccoInAttesa && (
                                <div className="mt-4 bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                                    <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                                        <AlertCircle size={18}/>
                                        Eliminazione non possibile
                                    </p>
                                    <p className="text-sm text-amber-900 mt-2 leading-relaxed">{bloccoInAttesa}</p>
                                </div>
                            )}
                            {!preflightLoading && preflight && haEsposizione && (
                                <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4">
                                    <p className="text-sm font-bold text-red-900 flex items-center gap-2">
                                        <AlertCircle size={18}/>
                                        Questo mandato ha importi in sospeso.
                                    </p>
                                    <ul className="mt-2 space-y-1 text-sm font-semibold text-red-900">
                                        {preflight.haResiduo && (
                                            <li>· {formatCurrency(preflight.importoResiduo)} non riscossi dal cliente</li>
                                        )}
                                        {preflight.haDaFatturare && (
                                            <li>· {formatCurrency(preflight.importoDaFatturare)} mai fatturati</li>
                                        )}
                                    </ul>
                                    <p className="text-xs text-red-800/90 mt-2 leading-relaxed">
                                        Concordato {formatCurrency(preflight.importoTotale)}, incassato {formatCurrency(preflight.importoIncassato)},
                                        fatturato {formatCurrency(preflight.importoFatturato)}.
                                        Eliminando il mandato questi importi escono dalla contabilità attiva. L'operazione resta
                                        registrata nel Registro Audit e continueranno a comparire in Contabilità fra i mandati
                                        con importi non riscossi o non fatturati, identificati dal solo numero di mandato.
                                    </p>
                                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={residuoAccettato}
                                            onChange={e => setResiduoAccettato(e.target.checked)}
                                            className="mt-0.5 w-4 h-4 accent-red-600 cursor-pointer"
                                        />
                                        <span className="text-sm font-semibold text-red-900">
                                            Ho letto: sto eliminando un mandato con importi ancora in sospeso.
                                        </span>
                                    </label>
                                </div>
                            )}
                            {/*
                              Di default preventivi/proforma collegati vengono scollegati e restano
                              in /preventivi, così non si perdono documenti già inviati al cliente.
                            */}
                            <label className="mt-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100/60 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={cascadePreventiviOnDelete}
                                    onChange={e => setCascadePreventiviOnDelete(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 accent-rose-600 cursor-pointer"
                                />
                                <div className="text-sm text-amber-900 leading-relaxed">
                                    <strong>Elimina anche i preventivi/proforma collegati a questo mandato.</strong>
                                    <div className="text-xs text-amber-700 mt-0.5">
                                        Lasciando vuoto, i documenti collegati verranno solo <em>scollegati</em> dal mandato e resteranno visibili in <code>/preventivi</code> come standalone.
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-100 shrink-0">
                            <button
                                onClick={() => { setPraticaToDelete(null); setCascadePreventiviOnDelete(false); }}
                                disabled={isDeleting}
                                className="px-5 py-2.5 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 font-bold transition-colors disabled:opacity-50 shadow-sm"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleteBloccato}
                                title={bloccoInAttesa ? bloccoInAttesa : haEsposizione && !residuoAccettato
                                    ? 'Spunta la presa d\'atto sugli importi in sospeso per procedere'
                                    : undefined}
                                className="px-5 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 font-bold flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                            >
                                {isDeleting ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
                                {isDeleting
                                    ? 'Eliminazione in corso...'
                                    : haEsposizione ? 'Elimina comunque' : 'Conferma Eliminazione'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PraticheView;
